import { z } from 'zod';

import { periodSchema } from '../pages/profile/periods';

export const mediaKindSchema = z.enum(['POST', 'REEL', 'STORY']);
export const mediaEnablementSchema = z.enum(['on', 'off', 'auto']);

/** `CataloguePriceOut` (`schemas/catalogue_prices.py`): the same shape on
 * every one of the three listing routes. `credits_per_day` is the total, not
 * `stats_credits_per_day + comments_credits_per_day` -- read as its own
 * field, never derived, so an enrichment or inference line the server folds
 * into the total is not silently dropped by a client that re-adds the two
 * halves itself. */
export const priceSchema = z.object({
  // Platform credits on the rate card; `null` where an operation has no rate.
  credits_per_day: z.number().nullable(),
  stats_credits_per_day: z.number().nullable(),
  comments_credits_per_day: z.number().nullable(),
  unpriced: z.array(z.string()),
  basis: z.string(),
  shape: z.object({ rate_per_day: z.number(), comments_per_item: z.number() }),
});
export type Price = z.infer<typeof priceSchema>;

export const profileMediaSchema = z.object({
  id: z.string(),
  media_kind: mediaKindSchema,
  enabled: mediaEnablementSchema,
  periods: z.array(periodSchema).default([]),
  story_interval_seconds: z.number().int().nullable().default(null),
  comments_enabled: z.boolean(),
  likers_enabled: z.boolean().default(false),
  comment_cap_per_post: z.number().int().nullable(),
});

/** Audio is a media kind of the profile itself: the periods a track's reuse list is read
 *  through from the source post's publication. `page_limit: null` reads to the end. */
export const profileAudioSchema = z.object({
  enabled: z.boolean(),
  periods: z.array(periodSchema),
  page_limit: z.number().int().nullable(),
});

/** `MonitoringProfileOut`: what the create, blank-create, update and active
 *  PATCH routes answer, unpriced. `active` and the `night_reduction_*`
 *  fields are not the only reader of that model -- see the module doc for
 *  why they default rather than require. `active` defaults to `false`, the
 *  column's own server default for a tenant-created row (`0076`). */
export const monitoringProfileOutSchema = z.object({
  id: z.string(),
  tenant_id: z.string().nullable(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  active: z.boolean().default(false),
  night_reduction_enabled: z.boolean().default(false),
  night_reduction_max_interval_seconds: z.number().int().nullable().default(null),
  night_reduction_start_local: z.string().nullable().default(null),
  night_reduction_stop_local: z.string().nullable().default(null),
  media: z.array(profileMediaSchema),
  audio: profileAudioSchema.default({ enabled: false, periods: [], page_limit: null }),
});
export type MonitoringProfileOut = z.infer<typeof monitoringProfileOutSchema>;

/** `MonitoringProfilePricedOut`. */
export const monitoringProfileSchema = monitoringProfileOutSchema.extend({
  price: priceSchema,
});
export type MonitoringProfilePriced = z.infer<typeof monitoringProfileSchema>;

/** `ProfileUsageExampleOut` (`schemas/monitoring_profiles.py`). */
export const profileUsageExampleSchema = z.object({
  id: z.string(),
  handle: z.string().nullable(),
  label: z.string().nullable(),
  platform: z.enum(['instagram', 'tiktok']),
  avatar_url: z.string().nullable(),
});
export type ProfileUsageExample = z.infer<typeof profileUsageExampleSchema>;

/** `MonitoringProfileListedOut` (`schemas/monitoring_profiles.py`): read-time facts the
 *  single-profile write routes do not return. `interval_seconds` is the tightest interval any
 *  enabled media resolves to, `null` when every kind is off; `usage_count` is the number of
 *  tracked accounts naming the profile, `usage_credits_per_day` their estimated daily cost
 *  together, and `usage_examples` up to five of them by handle. */
export const monitoringProfileListedSchema = monitoringProfileSchema.extend({
  interval_seconds: z.number().int().nullable(),
  usage_count: z.number().int(),
  usage_credits_per_day: z.number().nullable(),
  usage_examples: z.array(profileUsageExampleSchema),
});
export type MonitoringProfileListed = z.infer<typeof monitoringProfileListedSchema>;

export const mediaRowSchema = z.object({
  media_kind: z.enum(['POST', 'REEL', 'STORY']),
  enabled: z.enum(['on', 'off', 'auto']),
  periods: z.array(periodSchema),
  story_interval_seconds: z.number().int().nullable(),
  comments_enabled: z.boolean(),
  likers_enabled: z.boolean().default(false),
  comment_cap_per_post: z.number().nullable(),
});

/** The fields of `MonitoringProfileListedOut` this page reads; zod drops the rest. */
export const profileSchema = z.object({
  id: z.string(),
  tenant_id: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  media: z.array(mediaRowSchema),
  audio: profileAudioSchema,
  night_reduction_enabled: z.boolean(),
  night_reduction_max_interval_seconds: z.number().int().nullable(),
  night_reduction_start_local: z.string().nullable(),
  night_reduction_stop_local: z.string().nullable(),
  usage_count: z.number().int(),
  price: priceSchema,
});

export const profileListSchema = z.object({ items: z.array(profileSchema) });

export const profileAccountSchema = z.object({
  id: z.string(),
  handle: z.string().nullable(),
  campaign_name: z.string(),
});
export const accountsPageSchema = z.object({
  items: z.array(profileAccountSchema),
  total: z.number().int(),
});
